import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { FileDown, FileText } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Card } from '@/components/Card/Card';
import { Table } from '@/components/Table/Table';
import {
  fetchGiroEstoqueReport,
  relatorioGiroEstoqueKeys,
} from '@/services/relatorioGiroEstoqueService';
import type {
  IRelatorioGiroEstoqueFiltros,
  IRelatorioGiroEstoqueFiltrosAvancados,
  IRelatorioGiroEstoqueResponse,
  IRelatorioGiroEstoqueSearch,
} from '@/types/relatorioGiroEstoque';
import { RelatorioGiroEstoqueFiltrosForm, RelatorioGiroEstoqueForm } from './form';
import './giro-estoque.css';

export const Route = createFileRoute('/_authenticated/relatorio/giro-estoque/')({
  component: RelatorioGiroEstoquePage,
});

const INITIAL_FILTROS: IRelatorioGiroEstoqueFiltros = {
  dataInicio: '',
  dataFim: '',
};

const INITIAL_FILTROS_AVANCADOS: IRelatorioGiroEstoqueFiltrosAvancados = {
  filtroTexto: '',
  filtroMinMovimentacao: '',
  filtroMaxMovimentacao: '',
  filtroMinEstoque: '',
  filtroMaxEstoque: '',
  filtroMinDiasSemSaida: '',
  filtroMaxDiasSemSaida: '',
};

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const EMPTY_ROWS: IRelatorioGiroEstoqueResponse['dados'] = [];

function formatarData(data: string): string {
  if (!data) return '';
  return new Date(data).toLocaleDateString('pt-BR');
}

function formatarNumero(valor: number): string {
  return valor.toLocaleString('pt-BR');
}

function formatarCompacto(valor: number): string {
  if (valor >= 1_000_000) {
    return `${(valor / 1_000_000).toLocaleString('pt-BR', {
      maximumFractionDigits: 1,
    })}M`;
  }
  if (valor >= 1_000) {
    return `${(valor / 1_000).toLocaleString('pt-BR', {
      maximumFractionDigits: 1,
    })}K`;
  }
  return formatarNumero(valor);
}

function calcularDiasPeriodo(inicio: string, fim: string): string {
  if (!inicio || !fim) return '—';
  const start = new Date(inicio);
  const end = new Date(fim);
  const diffMs = end.getTime() - start.getTime();
  const dias = Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1);
  return `${dias} Dias`;
}

function formatarPosicao(index: number): string {
  return `${index + 1}º`;
}

function getPaginationPages(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [1];

  if (currentPage > 3) {
    pages.push('ellipsis');
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (currentPage < totalPages - 2) {
    pages.push('ellipsis');
  }

  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

function buildSearch(
  filtros: IRelatorioGiroEstoqueFiltros,
  filtrosAvancados: IRelatorioGiroEstoqueFiltrosAvancados,
): IRelatorioGiroEstoqueSearch {
  return { ...filtros, ...filtrosAvancados };
}

function exportarExcel(
  dados: IRelatorioGiroEstoqueResponse,
  search: IRelatorioGiroEstoqueSearch,
): void {
  if (!dados.dados.length) return;

  const wb = XLSX.utils.book_new();

  const dadosRelatorio = dados.dados.map((item, index) => ({
    Posição: index + 1,
    Código: item.codigo_produto,
    Descrição: item.descricao,
    'Entrada (Período)': item.entrada_periodo,
    'Saída (Período)': item.saida_periodo,
    'Movimentação Total': item.movimento_total,
    'Saldo em Estoque': item.saldo_estoque,
    'Dias sem Saída': item.dias_sem_saida,
    'Última Saída': item.ultima_saida,
    'Última Saída Histórica': item.ultima_saida_historica,
    'Primeira Entrada': item.primeira_entrada,
    'Média Entrada Mensal': item.media_entrada_mensal,
    'Maior Entrada Mensal': item.maior_entrada_mensal,
    'Mês Maior Entrada': item.mes_maior_entrada,
    'Maior Saída Mensal': item.maior_saida_mensal,
    'Mês Maior Saída': item.mes_maior_saida,
    'Total Registros': item.total_registros,
  }));

  const ws = XLSX.utils.json_to_sheet(dadosRelatorio);
  ws['!cols'] = [
    { wch: 8 },
    { wch: 15 },
    { wch: 30 },
    { wch: 18 },
    { wch: 18 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 20 },
    { wch: 15 },
    { wch: 18 },
    { wch: 18 },
    { wch: 15 },
    { wch: 18 },
    { wch: 15 },
    { wch: 12 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, 'Relatório Giro Estoque');

  const totais = dados.totais;
  const resumoData = [
    ['RESUMO DO RELATÓRIO DE GIRO DE ESTOQUE'],
    [''],
    ['Período:', `${formatarData(dados.periodo.inicio)} até ${formatarData(dados.periodo.fim)}`],
    ['Total de Produtos:', totais?.total_produtos ?? dados.dados.length],
    ['Total Entrada:', totais?.total_entrada ?? 0],
    ['Total Saída:', totais?.total_saida ?? 0],
    ['Total Movimentação:', totais?.total_movimentacao ?? 0],
    [''],
    ['PRODUTOS COM MENOR MOVIMENTAÇÃO (TOP 10)'],
    [''],
    ...dados.dados.slice(0, 10).map((item, index) => [
      `${index + 1}. ${item.codigo_produto}`,
      `${item.descricao} - Movimentação: ${item.movimento_total}`,
    ]),
  ];

  const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
  wsResumo['!cols'] = [{ wch: 25 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo');

  const fileName = `relatorio_giro_estoque_${search.dataInicio}_${search.dataFim}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

function RelatorioGiroEstoquePage() {
  const [filtros, setFiltros] = useState<IRelatorioGiroEstoqueFiltros>(INITIAL_FILTROS);
  const [filtrosAvancados, setFiltrosAvancados] = useState<IRelatorioGiroEstoqueFiltrosAvancados>(
    INITIAL_FILTROS_AVANCADOS,
  );
  const [activeSearch, setActiveSearch] = useState<IRelatorioGiroEstoqueSearch | null>(null);
  const [validationError, setValidationError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [vistaDetalhada, setVistaDetalhada] = useState(false);

  const {
    data: reportData,
    isFetching: isGenerating,
    isError: isReportError,
    error: reportError,
  } = useQuery({
    queryKey: activeSearch
      ? relatorioGiroEstoqueKeys.report(activeSearch)
      : [...relatorioGiroEstoqueKeys.all, 'report', 'idle'],
    queryFn: () => fetchGiroEstoqueReport(activeSearch!),
    enabled: Boolean(activeSearch),
    retry: false,
  });

  const validateAndSearch = (search: IRelatorioGiroEstoqueSearch) => {
    if (!search.dataInicio || !search.dataFim) {
      setValidationError('Por favor, preencha as datas de início e fim.');
      setActiveSearch(null);
      return;
    }

    if (new Date(search.dataInicio) > new Date(search.dataFim)) {
      setValidationError('A data de início deve ser anterior à data de fim.');
      setActiveSearch(null);
      return;
    }

    setValidationError('');
    setCurrentPage(1);
    setActiveSearch(search);
  };

  const handleGenerate = () => {
    validateAndSearch(buildSearch(filtros, filtrosAvancados));
  };

  const handleApplyFilters = () => {
    validateAndSearch(buildSearch(filtros, filtrosAvancados));
  };

  const handleClearFilters = () => {
    setFiltrosAvancados(INITIAL_FILTROS_AVANCADOS);
  };

  const errorMessage = (() => {
    if (validationError) return validationError;
    if (isReportError) {
      return reportError instanceof Error ? reportError.message : 'Erro ao gerar relatório';
    }
    return '';
  })();

  const hasResults = Boolean(activeSearch && reportData && !isGenerating);
  const rows = reportData?.dados ?? EMPTY_ROWS;
  const totalProdutos = reportData?.totais?.total_produtos ?? rows.length;
  const totalMovimentacao =
    reportData?.totais?.total_movimentacao ?? reportData?.total_geral ?? 0;
  const totalEntrada = reportData?.totais?.total_entrada ?? 0;
  const totalSaida = reportData?.totais?.total_saida ?? 0;

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);

  const paginationPages = getPaginationPages(currentPage, totalPages);
  const colSpan = vistaDetalhada ? 17 : 8;

  const rangeStart = rows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = rows.length === 0 ? 0 : Math.min(currentPage * pageSize, rows.length);
  const paginationHint =
    rows.length === 0
      ? 'Visualizando 0 de 0'
      : `Visualizando ${rangeStart}–${rangeEnd} de ${rows.length.toLocaleString('pt-BR')}`;

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  return (
    <div className="relatorio-giro-estoque-container">
      <div className="relatorio-giro-estoque-content">
        <div className="relatorio-giro-estoque-top-grid">
          <RelatorioGiroEstoqueForm
            filtros={filtros}
            isGenerating={isGenerating}
            errorMessage={errorMessage}
            onFiltrosChange={setFiltros}
            onGenerate={handleGenerate}
          />

          <Card className="relatorio-giro-estoque-resumo-card">
            <Card.Header className="relatorio-giro-estoque-resumo-header">
              <div className="relatorio-giro-estoque-section-header">
                <FileText
                  size={18}
                  aria-hidden="true"
                />
                <h3>Resumo do Relatório</h3>
              </div>

              <button
                type="button"
                className="export-button export-button--excel"
                disabled={!hasResults || rows.length === 0 || !activeSearch}
                onClick={() => {
                  if (reportData && activeSearch) {
                    exportarExcel(reportData, activeSearch);
                  }
                }}
              >
                <FileDown
                  size={16}
                  aria-hidden="true"
                />
                Gerar Excel
              </button>
            </Card.Header>

            <Card.Content>
              <div className="relatorio-giro-estoque-resumo-grid">
                <div className="relatorio-giro-estoque-resumo-item">
                  <span className="relatorio-giro-estoque-resumo-label">Período</span>
                  <span className="relatorio-giro-estoque-resumo-value">
                    {hasResults && reportData
                      ? calcularDiasPeriodo(reportData.periodo.inicio, reportData.periodo.fim)
                      : '—'}
                  </span>
                </div>
                <div className="relatorio-giro-estoque-resumo-item">
                  <span className="relatorio-giro-estoque-resumo-label">Produtos</span>
                  <span className="relatorio-giro-estoque-resumo-value">
                    {hasResults ? formatarNumero(totalProdutos) : '—'}
                  </span>
                </div>
                <div className="relatorio-giro-estoque-resumo-item">
                  <span className="relatorio-giro-estoque-resumo-label">Total Mov.</span>
                  <span className="relatorio-giro-estoque-resumo-value">
                    {hasResults ? formatarCompacto(totalMovimentacao) : '—'}
                  </span>
                </div>
                <div className="relatorio-giro-estoque-resumo-item">
                  <span className="relatorio-giro-estoque-resumo-label relatorio-giro-estoque-resumo-label--entrada">
                    Entradas
                  </span>
                  <span className="relatorio-giro-estoque-resumo-value relatorio-giro-estoque-resumo-value--entrada">
                    {hasResults ? formatarCompacto(totalEntrada) : '—'}
                  </span>
                </div>
                <div className="relatorio-giro-estoque-resumo-item">
                  <span className="relatorio-giro-estoque-resumo-label relatorio-giro-estoque-resumo-label--saida">
                    Saídas
                  </span>
                  <span className="relatorio-giro-estoque-resumo-value relatorio-giro-estoque-resumo-value--saida">
                    {hasResults ? formatarCompacto(totalSaida) : '—'}
                  </span>
                </div>
              </div>
            </Card.Content>
          </Card>
        </div>

        {hasResults && reportData && (
          <>
            <RelatorioGiroEstoqueFiltrosForm
              filtrosAvancados={filtrosAvancados}
              isGenerating={isGenerating}
              onFiltrosAvancadosChange={setFiltrosAvancados}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
            />

            <Card className="relatorio-giro-estoque-table-card">
              <Card.Header className="relatorio-giro-estoque-table-header">
                <h3>Resultados Analíticos</h3>
                <button
                  type="button"
                  className={[
                    'relatorio-giro-estoque-vista-btn',
                    vistaDetalhada ? 'relatorio-giro-estoque-vista-btn--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setVistaDetalhada((prev) => !prev)}
                >
                  {vistaDetalhada ? 'Vista Resumida' : 'Vista Detalhada'}
                </button>
              </Card.Header>

              <Card.Content>
                <div className="relatorio-giro-estoque-table-container">
                  <Table>
                    <caption className="sr-only">
                      Relatório de giro de estoque, resultados analíticos
                    </caption>
                    <Table.Header>
                      <Table.Row>
                        <Table.Head scope="col">Posição</Table.Head>
                        <Table.Head scope="col">Código</Table.Head>
                        <Table.Head scope="col">Descrição</Table.Head>
                        <Table.Head scope="col">Entrada (Per.)</Table.Head>
                        <Table.Head scope="col">Saída (Per.)</Table.Head>
                        <Table.Head scope="col">Mov. Total</Table.Head>
                        <Table.Head scope="col">Saldo</Table.Head>
                        <Table.Head scope="col">Dias s/ Saída</Table.Head>
                        {vistaDetalhada && (
                          <>
                            <Table.Head scope="col">Última Saída</Table.Head>
                            <Table.Head scope="col">Última Saída Hist.</Table.Head>
                            <Table.Head scope="col">Primeira Entrada</Table.Head>
                            <Table.Head scope="col">Média Entrada Mensal</Table.Head>
                            <Table.Head scope="col">Maior Entrada Mensal</Table.Head>
                            <Table.Head scope="col">Mês Maior Entrada</Table.Head>
                            <Table.Head scope="col">Maior Saída Mensal</Table.Head>
                            <Table.Head scope="col">Mês Maior Saída</Table.Head>
                            <Table.Head scope="col">Total Registros</Table.Head>
                          </>
                        )}
                      </Table.Row>
                    </Table.Header>
                    <Table.Content>
                      {paginatedData.length > 0 ? (
                        paginatedData.map((item, index) => {
                          const absoluteIndex = (currentPage - 1) * pageSize + index;
                          return (
                            <Table.Row key={`${item.codigo_produto}-${absoluteIndex}`}>
                              <Table.Cell className="relatorio-giro-estoque-pos">
                                {formatarPosicao(absoluteIndex)}
                              </Table.Cell>
                              <Table.Cell>
                                <span className="relatorio-giro-estoque-codigo">
                                  {item.codigo_produto}
                                </span>
                              </Table.Cell>
                              <Table.Cell className="relatorio-giro-estoque-descricao">
                                {item.descricao}
                              </Table.Cell>
                              <Table.Cell className="relatorio-giro-estoque-numero">
                                {formatarNumero(item.entrada_periodo)}
                              </Table.Cell>
                              <Table.Cell className="relatorio-giro-estoque-numero">
                                {formatarNumero(item.saida_periodo)}
                              </Table.Cell>
                              <Table.Cell className="relatorio-giro-estoque-numero relatorio-giro-estoque-mov-total">
                                {formatarNumero(item.movimento_total)}
                              </Table.Cell>
                              <Table.Cell className="relatorio-giro-estoque-numero">
                                {formatarNumero(item.saldo_estoque)}
                              </Table.Cell>
                              <Table.Cell className="relatorio-giro-estoque-numero">
                                {item.dias_sem_saida}
                              </Table.Cell>
                              {vistaDetalhada && (
                                <>
                                  <Table.Cell className="relatorio-giro-estoque-numero">
                                    {item.ultima_saida}
                                  </Table.Cell>
                                  <Table.Cell className="relatorio-giro-estoque-numero">
                                    {item.ultima_saida_historica}
                                  </Table.Cell>
                                  <Table.Cell className="relatorio-giro-estoque-numero">
                                    {item.primeira_entrada}
                                  </Table.Cell>
                                  <Table.Cell className="relatorio-giro-estoque-numero">
                                    {formatarNumero(item.media_entrada_mensal)}
                                  </Table.Cell>
                                  <Table.Cell className="relatorio-giro-estoque-numero">
                                    {formatarNumero(item.maior_entrada_mensal)}
                                  </Table.Cell>
                                  <Table.Cell className="relatorio-giro-estoque-numero">
                                    {item.mes_maior_entrada}
                                  </Table.Cell>
                                  <Table.Cell className="relatorio-giro-estoque-numero">
                                    {formatarNumero(item.maior_saida_mensal)}
                                  </Table.Cell>
                                  <Table.Cell className="relatorio-giro-estoque-numero">
                                    {item.mes_maior_saida}
                                  </Table.Cell>
                                  <Table.Cell className="relatorio-giro-estoque-numero">
                                    {item.total_registros}
                                  </Table.Cell>
                                </>
                              )}
                            </Table.Row>
                          );
                        })
                      ) : (
                        <Table.Row>
                          <Table.Cell
                            colSpan={colSpan}
                            className="relatorio-giro-estoque-no-data"
                          >
                            Nenhum dado encontrado para o período selecionado.
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Content>
                  </Table>
                </div>
              </Card.Content>

              {rows.length > 0 && (
                <Card.Footer className="relatorio-giro-estoque-pagination">
                  <div className="relatorio-giro-estoque-pagination__left">
                    <p>
                      Total de Registros:{' '}
                      <strong>{rows.length.toLocaleString('pt-BR')}</strong>
                    </p>
                    <label htmlFor="giro-estoque-page-size">Linhas por pág:</label>
                    <select
                      id="giro-estoque-page-size"
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option
                          key={size}
                          value={size}
                        >
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="relatorio-giro-estoque-pagination__right">
                    <span className="relatorio-giro-estoque-pagination__hint">
                      {paginationHint}
                    </span>
                    <div className="relatorio-giro-estoque-pagination__controls">
                      <button
                        type="button"
                        className="relatorio-giro-estoque-page-btn"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                        aria-label="Primeira página"
                      >
                        |&lt;
                      </button>
                      <button
                        type="button"
                        className="relatorio-giro-estoque-page-btn"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                        aria-label="Página anterior"
                      >
                        &lt;
                      </button>

                      {paginationPages.map((page, index) =>
                        page === 'ellipsis' ? (
                          <span
                            key={`ellipsis-${index}`}
                            className="relatorio-giro-estoque-page-ellipsis"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            type="button"
                            className={[
                              'relatorio-giro-estoque-page-btn',
                              page === currentPage
                                ? 'relatorio-giro-estoque-page-btn--active'
                                : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        ),
                      )}

                      <button
                        type="button"
                        className="relatorio-giro-estoque-page-btn"
                        disabled={currentPage >= totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                        aria-label="Próxima página"
                      >
                        &gt;
                      </button>
                      <button
                        type="button"
                        className="relatorio-giro-estoque-page-btn"
                        disabled={currentPage >= totalPages}
                        onClick={() => handlePageChange(totalPages)}
                        aria-label="Última página"
                      >
                        &gt;|
                      </button>
                    </div>
                  </div>
                </Card.Footer>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
